import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import SettingsIcon from '@material-ui/icons/Settings';
import NotificationsIcon from '@material-ui/icons/Notifications';
import {
  Field,
  Input,
  Textarea,
  Checkbox,
  RadioGroup,
  Switch,
  Select,
} from '../form';
import {
  Button,
  IconButton,
  Spinner,
  Surface,
  Badge,
  Divider,
  Avatar,
  Chip,
} from '../primitives';
import { useDsTokens } from '../theme/useDsTokens';

const useStyles = makeStyles((theme) => ({
  page: {
    padding: theme.spacing(3),
    maxWidth: 720,
    margin: '0 auto',
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    fontSize: 14,
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  buttonRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  iconRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  spinnerRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    alignItems: 'center',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  surfaceGrid: {
    display: 'grid',
    gap: theme.spacing(2),
  },
}));

/**
 * Isolated demo for Design System components.
 * Route: /design-system-demo (logged-in, not linked in nav)
 */
export default function FormComponentsDemo() {
  const classes = useStyles();
  const tokens = useDsTokens();

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [priority, setPriority] = useState('medium');
  const [notifications, setNotifications] = useState(true);
  const [queue, setQueue] = useState('');
  const [showError, setShowError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterActive, setFilterActive] = useState(false);
  const [tags, setTags] = useState(['Suporte', 'VIP']);

  const nameError = showError && name.trim().length === 0;

  const handleSave = () => {
    setSaving(true);
    window.setTimeout(() => setSaving(false), 1500);
  };

  return (
    <div
      className={classes.page}
      style={{ backgroundColor: tokens.surfaceApp, minHeight: '100vh' }}
    >
      <h1 className={classes.title} style={{ color: tokens.textPrimary }}>
        Design System — Demo
      </h1>
      <p className={classes.subtitle} style={{ color: tokens.textSecondary }}>
        Sprints 2.2–2.4 — Form + 8 Core Primitives L3
      </p>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle} style={{ color: tokens.textSecondary }}>
          Button
        </h2>
        <div className={classes.buttonRow}>
          <Button variant="contained" intent="primary">
            Primary
          </Button>
          <Button variant="contained" intent="secondary">
            Secondary
          </Button>
          <Button variant="outlined" intent="primary">
            Outlined
          </Button>
          <Button variant="text" intent="primary">
            Text
          </Button>
          <Button variant="ghost" intent="secondary">
            Ghost
          </Button>
          <Button variant="contained" intent="danger">
            Excluir
          </Button>
          <Button size="dense" variant="contained">
            Dense
          </Button>
          <Button disabled>Disabled</Button>
          <Button loading={saving} onClick={handleSave}>
            {saving ? 'Salvando' : 'Salvar'}
          </Button>
          <Button startIcon={<SaveIcon />}>Com ícone</Button>
        </div>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle} style={{ color: tokens.textSecondary }}>
          Icon Button
        </h2>
        <div className={classes.iconRow}>
          <IconButton aria-label="Configurações" variant="default">
            <SettingsIcon fontSize="small" />
          </IconButton>
          <IconButton aria-label="Salvar" variant="primary">
            <SaveIcon fontSize="small" />
          </IconButton>
          <IconButton aria-label="Excluir linha" variant="danger">
            <DeleteIcon fontSize="small" />
          </IconButton>
          <IconButton aria-label="Ação ghost" variant="ghost">
            <SettingsIcon fontSize="small" />
          </IconButton>
          <IconButton aria-label="Dense" size="dense" variant="primary">
            <SaveIcon style={{ fontSize: 16 }} />
          </IconButton>
          <IconButton aria-label="Desabilitado" disabled>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle} style={{ color: tokens.textSecondary }}>
          Spinner
        </h2>
        <div className={classes.spinnerRow}>
          <Spinner size="sm" label="Pequeno" />
          <Spinner size="md" label="Médio" />
          <Spinner size="lg" label="Grande" />
        </div>
        <div style={{ marginTop: 16, border: `1px solid ${tokens.borderDefault}`, borderRadius: 8 }}>
          <Spinner variant="centered" label="Carregando seção" />
        </div>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle} style={{ color: tokens.textSecondary }}>
          Surface
        </h2>
        <div className={classes.surfaceGrid}>
          <Surface variant="raised" elevated padding="md">
            Raised elevated card
          </Surface>
          <Surface variant="sunken" padding="sm">
            Sunken inset area
          </Surface>
          <Surface variant="raised" interactive padding="md">
            Interactive raised (hover)
          </Surface>
        </div>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle} style={{ color: tokens.textSecondary }}>
          Badge
        </h2>
        <div className={classes.iconRow}>
          <Badge badgeContent={12}>
            <IconButton aria-label="Notificações, 12 não lidas">
              <NotificationsIcon fontSize="small" />
            </IconButton>
          </Badge>
          <Badge variant="dot">
            <IconButton aria-label="Nova atividade">
              <NotificationsIcon fontSize="small" />
            </IconButton>
          </Badge>
          <Badge variant="status-success" badgeContent={1}>
            <Avatar size="md">JS</Avatar>
          </Badge>
        </div>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle} style={{ color: tokens.textSecondary }}>
          Divider
        </h2>
        <Divider />
        <Divider variant="inset" />
        <div style={{ display: 'flex', height: 32, alignItems: 'center', gap: 8 }}>
          <span style={{ color: tokens.textSecondary, fontSize: 14 }}>Grupo A</span>
          <Divider orientation="vertical" decorative />
          <span style={{ color: tokens.textSecondary, fontSize: 14 }}>Grupo B</span>
        </div>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle} style={{ color: tokens.textSecondary }}>
          Avatar
        </h2>
        <div className={classes.iconRow}>
          <Avatar size="sm" aria-label="Ana">AN</Avatar>
          <Avatar size="md" aria-label="Bruno">BR</Avatar>
          <Avatar size="lg" aria-label="Carla">CA</Avatar>
          <Avatar size="md" loading aria-label="Carregando avatar" />
          <Badge variant="dot">
            <Avatar size="md" aria-label="Online">ON</Avatar>
          </Badge>
        </div>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle} style={{ color: tokens.textSecondary }}>
          Chip
        </h2>
        <div className={classes.chipRow}>
          <Chip label="Neutral" />
          <Chip label="Success" variant="status-success" />
          <Chip label="Warning" variant="status-warning" />
          <Chip label="Error" variant="status-error" />
          <Chip
            label="Filtro"
            selected={filterActive}
            onClick={() => setFilterActive(!filterActive)}
          />
          <Chip
            label="Fila Vendas"
            variant="queue"
            queueColor="#0D9488"
          />
          {tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              onDelete={() => setTags((t) => t.filter((x) => x !== tag))}
            />
          ))}
        </div>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle} style={{ color: tokens.textSecondary }}>
          Form components
        </h2>
        <div className={classes.row}>
          <Field
            label="Nome"
            description="Nome completo do contato"
            error={nameError}
            errorMessage={nameError ? 'Nome é obrigatório' : undefined}
            required
          >
            <Input
              value={name}
              placeholder="Digite o nome"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field label="Observações">
            <Textarea
              value={notes}
              placeholder="Notas adicionais…"
              minRows={3}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>

          <Field label="Desabilitado">
            <Input value="Valor fixo" disabled />
          </Field>

          <Checkbox
            label="Aceito os termos de uso"
            checked={acceptTerms}
            onChange={(_, checked) => setAcceptTerms(checked)}
          />

          <Field label="Prioridade">
            <RadioGroup
              name="priority"
              value={priority}
              options={[
                { value: 'low', label: 'Baixa' },
                { value: 'medium', label: 'Média' },
                { value: 'high', label: 'Alta' },
              ]}
              onChange={(e) => setPriority(e.target.value)}
            />
          </Field>

          <Switch
            label="Notificações ativas"
            checked={notifications}
            onChange={(_, checked) => setNotifications(checked)}
          />

          <Field label="Fila" required>
            <Select
              placeholder="Selecione uma fila"
              value={queue}
              options={[
                { value: 'support', label: 'Suporte' },
                { value: 'sales', label: 'Vendas' },
                { value: 'billing', label: 'Financeiro', disabled: true },
              ]}
              onChange={(e) => setQueue(e.target.value as string)}
            />
          </Field>
        </div>
      </section>

      <section className={classes.section}>
        <Button variant="outlined" intent="primary" onClick={() => setShowError(true)}>
          Validar formulário
        </Button>
      </section>
    </div>
  );
}
